import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Events.scss';

interface EventData {
  event_id: string;
  title: string;
  description: string;
  is_active: boolean;
  start_seconds_unix: number;
  end_seconds_unix: number;
  game: number; // 0=Ludo, 1=Solitaire, 2=Quiz
  game_mode: number;
  win_under_seconds: number;
  reward: {
    type: number;
    amount: number;
  };
}

interface EventsData {
  events: Record<string, EventData>;
}

const Events: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [events, setEvents] = useState<EventData[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  // Form state
  const [formData, setFormData] = useState<Partial<EventData>>({
    event_id: '',
    title: '',
    description: '',
    is_active: true,
    start_seconds_unix: 0,
    end_seconds_unix: 0,
    game: 0,
    game_mode: 0,
    win_under_seconds: 0,
    reward: { type: 0, amount: 100 }
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await consoleService.callRpcFunction({
        id: 'event_get_all',
        body: JSON.stringify({}),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      if (response.body) {
        const data: EventsData = JSON.parse(response.body);
        const eventsList = data.events ? Object.values(data.events) : [];
        setEvents(eventsList);
      } else {
        setEvents([]);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    try {
      if (!formData.event_id || !formData.title) {
        setError('Event ID and Title are required');
        return;
      }

      setLoading(true);
      setError(null);

      await consoleService.callRpcFunction({
        id: 'event_add',
        body: JSON.stringify(formData),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      setShowCreateModal(false);
      setEditingEventId(null);
      resetForm();
      await loadEvents();
      setSuccessMessage('Event created successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = async () => {
    try {
      if (!formData.title) {
        setError('Title is required');
        return;
      }

      setLoading(true);
      setError(null);

      // Build the update payload using the CURRENT state values
      const updatePayload = {
        event_id: editingEventId || '',
        title: formData.title || '',
        description: formData.description || '',
        is_active: formData.is_active ?? true,
        start_seconds_unix: formData.start_seconds_unix ?? 0,
        end_seconds_unix: formData.end_seconds_unix ?? 0,
        game: formData.game ?? 0,
        game_mode: formData.game_mode ?? 0,
        win_under_seconds: formData.win_under_seconds ?? 0,
        reward: formData.reward ?? { type: 0, amount: 0 }
      };

      if (!updatePayload.event_id) {
        setError('No event selected for editing');
        return;
      }

      await consoleService.callRpcFunction({
        id: 'event_update',
        body: JSON.stringify(updatePayload),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });

      // Update the event in local state immediately so the card reflects changes
      setEvents(prevEvents =>
        prevEvents.map(event =>
          event.event_id === updatePayload.event_id
            ? { ...event, ...updatePayload }
            : event
        )
      );

      setShowCreateModal(false);
      setEditingEventId(null);
      resetForm();
      setSuccessMessage('Event updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (event: EventData) => {
    setFormData({
      event_id: event.event_id,
      title: event.title,
      description: event.description,
      is_active: event.is_active,
      start_seconds_unix: event.start_seconds_unix,
      end_seconds_unix: event.end_seconds_unix,
      game: event.game,
      game_mode: event.game_mode,
      win_under_seconds: event.win_under_seconds,
      reward: { type: event.reward?.type || 0, amount: event.reward?.amount || 0 }
    });
    setEditingEventId(event.event_id);
    setShowCreateModal(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    openConfirmDialog(
      'Delete Event',
      `Are you sure you want to delete event "${eventId}"?`,
      async () => {
        try {
          await consoleService.callRpcFunction({
            id: 'event_remove',
            body: JSON.stringify({ event_id: eventId }),
            user_id: '00000000-0000-0000-0000-000000000000',
            http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
          });
          await loadEvents();
          setSuccessMessage('Event deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.message || 'Failed to delete event');
        }
      }
    );
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingEventId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      event_id: '',
      title: '',
      description: '',
      is_active: true,
      start_seconds_unix: 0,
      end_seconds_unix: 0,
      game: 0,
      game_mode: 0,
      win_under_seconds: 0,
      reward: { type: 0, amount: 100 }
    });
  };

  const getGameName = (game: number): string => {
    switch (game) {
      case 0: return 'Ludo';
      case 1: return 'Solitaire';
      case 2: return 'Quiz';
      default: return 'Unknown';
    }
  };

  const getGameIcon = (game: number): string => {
    switch (game) {
      case 0: return '🎲';
      case 1: return '🃏';
      case 2: return '🧠';
      default: return '🎮';
    }
  };

  const formatDateIST = (unixSeconds: number): string => {
    if (!unixSeconds) return 'Not set';
    const date = new Date(unixSeconds * 1000);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const handleDateChange = (field: 'start_seconds_unix' | 'end_seconds_unix', dateString: string) => {
    const date = new Date(dateString);
    const unixSeconds = Math.floor(date.getTime() / 1000);
    setFormData(prev => ({ ...prev, [field]: unixSeconds }));
  };

  const unixToDatetimeLocal = (unixSeconds: number): string => {
    if (!unixSeconds) return '';
    const date = new Date(unixSeconds * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  if (loading && events.length === 0) {
    return (
      <div className="events-container">
        <div className="loading-spinner">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="events-container">
      <div className="events-header">
        <h1>📅 Events Management</h1>
        <p>Create and manage game events with rewards</p>
        <button
          className="btn btn-success"
          onClick={() => {
            resetForm();
            setEditingEventId(null);
            setShowCreateModal(true);
          }}
        >
          ➕ Create Event
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <div className="events-grid">
        {events.length > 0 ? (
          events.map((event) => (
            <div key={event.event_id} className={`event-card ${event.is_active ? 'active' : 'inactive'}`}>
              <div className="event-header">
                <span className="game-icon">{getGameIcon(event.game)}</span>
                <div className="event-title-section">
                  <h3>{event.title}</h3>
                  <span className={`status-badge ${event.is_active ? 'active' : 'inactive'}`}>
                    {event.is_active ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
              </div>

              <p className="event-description">{event.description || 'No description'}</p>

              <div className="event-details">
                <div className="detail-row">
                  <span className="label">ID:</span>
                  <span className="value event-id-value">{event.event_id}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Game:</span>
                  <span className="value">{getGameName(event.game)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Reward:</span>
                  <span className="value">💰 {event.reward?.amount || 0} coins</span>
                </div>
                <div className="detail-row">
                  <span className="label">Start:</span>
                  <span className="value">{formatDateIST(event.start_seconds_unix)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">End:</span>
                  <span className="value">{formatDateIST(event.end_seconds_unix)}</span>
                </div>
                {event.win_under_seconds > 0 && (
                  <div className="detail-row">
                    <span className="label">Win Under:</span>
                    <span className="value">{event.win_under_seconds}s</span>
                  </div>
                )}
              </div>

              <div className="event-actions">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openEditModal(event)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteEvent(event.event_id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No Events</h3>
            <p>Create your first event to get started</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEventId ? 'Edit Event' : 'Create New Event'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {editingEventId ? (
                <div className="form-group">
                  <label>Event ID</label>
                  <input
                    type="text"
                    value={formData.event_id || ''}
                    disabled={true}
                  />
                  <small className="form-text text-muted">Event ID cannot be changed after creation</small>
                </div>
              ) : (
                <div className="form-group">
                  <label>Event ID *</label>
                  <input
                    type="text"
                    value={formData.event_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_id: e.target.value }))}
                    placeholder="unique_event_id"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Event Title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Event description..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Game</label>
                  <select
                    value={formData.game || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, game: parseInt(e.target.value) }))}
                  >
                    <option value={0}>🎲 Ludo</option>
                    <option value={1}>🃏 Solitaire</option>
                    <option value={2}>🧠 Quiz</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Active</label>
                  <select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="datetime-local"
                    value={unixToDatetimeLocal(formData.start_seconds_unix || 0)}
                    onChange={(e) => handleDateChange('start_seconds_unix', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="datetime-local"
                    value={unixToDatetimeLocal(formData.end_seconds_unix || 0)}
                    onChange={(e) => handleDateChange('end_seconds_unix', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reward Amount (coins)</label>
                  <input
                    type="number"
                    value={formData.reward?.amount || 0}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      reward: { ...prev.reward!, amount: parseInt(e.target.value) || 0 }
                    }))}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Win Under (seconds, 0 = no limit)</label>
                  <input
                    type="number"
                    value={formData.win_under_seconds || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, win_under_seconds: parseInt(e.target.value) || 0 }))}
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={editingEventId ? handleEditEvent : handleCreateEvent}
                disabled={loading}
              >
                {loading ? 'Saving...' : editingEventId ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default Events;